import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
// Components
import { WordPopup, SearchBar } from './';
// store
import { setTimeStamp } from '../../features/video/videoSlice';
// Icons
import { GrCut } from 'react-icons/gr';
import { IoClose } from 'react-icons/io5';
import { MdOutlineCheckBoxOutlineBlank } from 'react-icons/md';
import { MdCheckBox } from 'react-icons/md';
import { groupConsecutiveBySpeaker } from '../../utils/transcriptUtils';

const TransciptContainer = ({ transcript, onGoToTimeStamp, miniTranscript }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Local states and refs
  const [showWordPopup, setShowWordPopup] = useState(false);
  const [popupPosition, setPopupPosition] = useState({ left: 0, top: 0 });
  const [selectedWord, setSelectedWord] = useState(null);
  const [clickedSpanId, setClickedSpanId] = useState(null);
  const [searcherResult, setSearcherResult] = useState([]);
  const [paragraphPosition, setParagraphPosition] = useState(0);
  const [isCutting, setIsCutting] = useState(false);
  const [startObj, setStartObj] = useState(null);
  const [endObj, setEndObj] = useState(null);
  const [searchTerm, setSearchTerm] = useState(null);
  const transcriptContainerRef = useRef();

  const selectedText = useSelector((state) => state.video.selectedText);
  const { video } = useSelector((state) => state.video);
  console.log('video', video);

  // Determine if the transcript is a flat transcript.
  // Flat: Each element is an object with a "text" property.
  const isFlatTranscript =
    transcript && transcript.length > 0 && transcript[0] && Object.prototype.hasOwnProperty.call(transcript[0], 'text');

  // EFFECT: Scroll to the paragraph when the search result position changes.
  useEffect(() => {
    if (searcherResult[paragraphPosition]?.paragraphIndex !== undefined) {
      scrollToParagraph(searcherResult[paragraphPosition].paragraphIndex);
    }
  }, [paragraphPosition, searcherResult]);

  // EFFECT: When both start and end are selected for clip creation, navigate to create-clip route.
  useEffect(() => {
    if (startObj && endObj) {
      const startTime = isFlatTranscript ? startObj.startTime : startObj.startTime / 10;
      const endTime = isFlatTranscript ? endObj.endTime : endObj.endTime / 10;

      navigate(
        `../create-clip/${video.VideoId}?start=${Math.round(startTime)}&end=${Math.round(endTime)}&sp=${startObj.position}&ep=${endObj.position}`,
        { replace: true }
      );
    }
  }, [startObj, endObj]);

  // EFFECT: Trigger search if the global selectedText changes.
  useEffect(() => {
    if (selectedText) handleSearch(selectedText);
  }, [transcript, selectedText]);

  // Utility function to escape RegExp special characters.
  const escapeRegExp = (string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  };

  // Highlight search term within text
  const highlightTextReact = (text, searchTerm) => {
    if (!searchTerm) return [text];
    const escapedTerm = escapeRegExp(searchTerm);
    const regex = new RegExp(escapedTerm, 'gi');
    let lastIndex = 0;
    const result = [];
    let match;
    let key = 0;

    while ((match = regex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        result.push(text.slice(lastIndex, match.index));
      }
      result.push(
        <span
          key={key++}
          style={{
            backgroundColor: 'yellow',
            display: 'inline',
            whiteSpace: 'normal',
          }}
        >
          {match[0]}
        </span>
      );
      lastIndex = regex.lastIndex;
    }
    if (lastIndex < text.length) {
      result.push(text.slice(lastIndex));
    }
    return result;
  };

  // Adapted handleSearch for both flat and nested transcript structures.
  // For flat transcripts, we search within the grouped text.
  const handleSearch = (term) => {
    if (!term) {
      setSearcherResult([]);
      return;
    }

    // Guard against undefined or non-array transcripts
    const data = Array.isArray(transcript) ? transcript : [];
    const isFlat = data.length > 0 && data[0] && Object.prototype.hasOwnProperty.call(data[0], 'text');

    const lowerTerm = term.toLowerCase();
    let result = [];

    if (isFlat) {
      // Group the flat transcript by speaker
      const groupedTranscript = groupConsecutiveBySpeaker(data);
      // Search within each group and record the paragraph index (here, the group index)
      result = groupedTranscript.reduce((acc, group, index) => {
        if (group.text.toLowerCase().includes(lowerTerm)) {
          acc.push({ paragraphIndex: index });
        }
        return acc;
      }, []);
    } else {
      result = data.reduce((acc, paragraph, index) => {
        let searchWords = term.split(' ');
        let positions = [];

        for (let i = 0; i < paragraph.length - searchWords.length + 1; i++) {
          let found = true;
          let tempPositions = [];
          for (let j = 0; j < searchWords.length; j++) {
            if (
              !paragraph[i + j].word
                .toLowerCase()
                .replace(/[^\w\s]/g, '')
                .startsWith(searchWords[j].toLowerCase().replace(/[^\w\s]/g, ''))
            ) {
              found = false;
              break;
            }
            tempPositions.push(i + j);
          }
          if (found) {
            positions.push(...tempPositions);
          }
        }

        if (positions.length > 0) {
          acc.push({ paragraphIndex: index, positions: positions });
        }

        return acc;
      }, []);
    }
    setParagraphPosition(0);
    setSearcherResult(result);
  };

  // Scroll to the specified paragraph (or group) element in the transcript container.
  const scrollToParagraph = (paragraphNumber) => {
    const paragraphElement = document.getElementById(`p-${paragraphNumber}`);
    const container = transcriptContainerRef.current;
    if (paragraphElement && container) {
      container.scrollTo({
        top: paragraphElement.offsetTop - container.offsetTop,
        behavior: 'smooth',
      });
    }
  };

  const handleChangeParagraphLocation = (direction) => {
    if (direction === 'down') {
      if (paragraphPosition < searcherResult.length - 1) setParagraphPosition(paragraphPosition + 1);
    } else if (direction === 'up') {
      if (paragraphPosition > 0) setParagraphPosition(paragraphPosition - 1);
    }
  };

  const onCloseWordPopup = () => {
    setShowWordPopup(false);
    if (clickedSpanId) {
      const spanElement = document.getElementById(clickedSpanId);
      if (spanElement) {
        spanElement.style.backgroundColor = '';
      }
      setClickedSpanId(null);
    }
  };

  // Handle right-click to show the WordPopup (works for both structures).
  const handleRightClick = (wordObj, event, i, j) => {
    event.preventDefault();
    if (clickedSpanId) {
      const prevSpan = document.getElementById(clickedSpanId);
      if (prevSpan) {
        prevSpan.style.backgroundColor = '';
      }
    }
    const rect = event.target.getBoundingClientRect();
    setPopupPosition({ left: rect.left, top: rect.y });
    setSelectedWord({ ...wordObj, position: `${i}-${j}` });
    setShowWordPopup(true);
    setClickedSpanId(event.target.id);
    event.target.style.backgroundColor = '#7FB3D5';
  };

  const handleSelectStart = () => {
    setIsCutting(true);
    setStartObj(selectedWord);
    setShowWordPopup(false);
  };

  const handleSelectEnd = () => {
    if (startObj) {
      setEndObj(selectedWord);
      setShowWordPopup(false);
    }
  };

  const handleCloseCutting = () => {
    setIsCutting(false);
    setStartObj(null);
    setEndObj(null);
  };

  const handleGoToTimeInMini = (timeVal) => {
    dispatch(setTimeStamp(timeVal));
  };

  // Render transcript function adapted:
  // For flat transcripts, we now group them by speaker using groupConsecutiveBySpeaker.
  const renderTranscript = () => {
    if (isFlatTranscript) {
      // Group the flat transcript by speaker
      const groupedTranscript = groupConsecutiveBySpeaker(transcript);

      console.log('HEY');
      return groupedTranscript.map((group, i) => (
        <p className="w-full flex flex-wrap" key={`p-${i}`} id={`p-${i}`}>
          <span
            id={`span-${i}-0`}
            className="whitespace-pre-wrap hover:bg-blue-100 cursor-pointer px-1 relative"
            onClick={(e) =>
              isCutting
                ? handleRightClick(group, e, i, 0)
                : miniTranscript
                  ? handleGoToTimeInMini(group.startTime)
                  : onGoToTimeStamp(group.startTime)
            }
            onContextMenu={(e) => !miniTranscript && handleRightClick(group, e, i, 0)}
          >
            <strong className="font-bold">Speaker {group.speaker}:</strong>{' '}
            {searchTerm ? highlightTextReact(group.text, searchTerm) : group.text}
          </span>
        </p>
      ));
    } else {
      // Nested transcript rendering remains unchanged.
      return transcript?.map((paragraph, pIndex) => (
        <React.Fragment key={pIndex}>
          {paragraph.length > 0 && (
            <p className="w-full flex flex-wrap" key={`p-${pIndex}`} id={`p-${pIndex}`}>
              {paragraph.map((wordObj, j) => {
                const isHighlighted = searcherResult?.some(
                  (item) => item.paragraphIndex === pIndex && item.positions.includes(j)
                );
                return (
                  <span
                    key={`span-${pIndex}-${j}`}
                    id={`span-${pIndex}-${j}`}
                    className={`whitespace-pre-wrap hover:bg-blue-100 cursor-pointer px-1 relative ${
                      isHighlighted ? 'bg-blue-200' : ''
                    }`}
                    onClick={(e) =>
                      isCutting
                        ? handleRightClick(wordObj, e, pIndex, j)
                        : miniTranscript
                          ? handleGoToTimeInMini(isFlatTranscript ? wordObj.startTime : wordObj.startTime / 10)
                          : onGoToTimeStamp(isFlatTranscript ? wordObj.startTime : wordObj.startTime / 10)
                    }
                    onContextMenu={(e) => !miniTranscript && handleRightClick(wordObj, e, pIndex, j)}
                  >
                    {searchTerm ? highlightTextReact(wordObj.word, searchTerm) : wordObj.word}
                  </span>
                );
              })}
            </p>
          )}
        </React.Fragment>
      ));
    }
  };

  return (
    <div className="h-full max-h-screen flex-1 flex flex-col py-[15px] px-2.5 sm:px-5 rounded-md border border-input bg-background">
      <div className="border-b border-border mb-5 pb-2.5">
        <h2
          id="Office-Information"
          className="font-playfair text-[28px] capitalize text-darkblue font-medium leading-8"
        >
          Video Transcript
        </h2>
      </div>
      {/* Show search bar and cutting controls if not in miniTranscript mode */}
      {!miniTranscript && (
        <div className="flex flex-col sm:flex-row items-center gap-[15px] mb-5">
          <SearchBar
            handleSearch={handleSearch}
            counter={searcherResult.length}
            location={paragraphPosition + 1}
            onChangeLocation={handleChangeParagraphLocation}
            placeholder="Search..."
            search={searchTerm}
            setSearch={setSearchTerm}
            autoSearch
          />
          <button
            className="py-[9px] px-8 bg-[#ECEFF4] text-base font-normal text-black transition-all duration-500 hover:bg-lightblue-hover hover:text-white"
            onClick={() => handleSearch(searchTerm)}
          >
            Search
          </button>
          <button
            className={`py-[9px] px-[13px] text-base font-normal transition-all duration-500 hover:bg-darkblue text-white ${
              isCutting ? 'bg-darkblue' : 'bg-primary'
            } p-1 rounded-sm mr-6`}
            onClick={() => setIsCutting(!isCutting)}
          >
            <GrCut className="w-6 h-6" />
          </button>
        </div>
      )}
      <div
        className="flex flex-col gap-5 px-6 py-3 border border-border bg-white overflow-y-auto overflow-x-hidden h-[calc(100vh-260px)]"
        ref={transcriptContainerRef}
      >
        {renderTranscript()}
        {showWordPopup && (
          <div
            style={{
              left: popupPosition.left,
              top: popupPosition.top + 30,
              position: 'fixed',
              zIndex: '50',
            }}
          >
            <WordPopup
              onClose={onCloseWordPopup}
              onGoToTimeStamp={() => onGoToTimeStamp(selectedWord.startTime)}
              handleSelectStart={handleSelectStart}
              handleSelectEnd={handleSelectEnd}
            />
          </div>
        )}
      </div>
      {isCutting && (
        <div className="fixed flex flex-col gap-2 bottom-0 right-0 bg-sky-100 shadow-2xl py-5 px-9 text-gray-700">
          <span className="flex gap-2">
            <GrCut className="w-6 h-6 text-blue-500" /> Click the transcript above to select the clip's start and end
            times
          </span>
          <div className="w-full flex gap-8 ml-[35px]">
            <span className="flex items-center gap-2">
              Start time {startObj ? <MdCheckBox className="text-blue-500" /> : <MdOutlineCheckBoxOutlineBlank />}
            </span>
            <span className="flex items-center gap-2">
              End time {endObj ? <MdCheckBox className="text-blue-500" /> : <MdOutlineCheckBoxOutlineBlank />}
            </span>
          </div>
          <button
            className="absolute text-gray-400 top-1 right-1 rounded-xl hover:bg-gray-200 hover:bg-opacity-35 p-1"
            onClick={handleCloseCutting}
          >
            <IoClose className="w-5 h-5" />
          </button>
        </div>
      )}
    </div>
  );
};

export default TransciptContainer;
